import BackButton from "@src/components/_shared/BackButton";
import Container from "@src/components/_shared/Container";
import BlockTitle from "@src/components/_shared/UI/BlockTitle";
import useNavigationStore from "@src/stores/navigationStore";
import useRegistrationStore from "@src/stores/registrationStore";
import { useMutation, useQuery } from '@tanstack/react-query';
import { getReferencialData } from '@src/api/Referencial';
import Loader from '@src/components/_shared/UI/Loader';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import DropdownList from '@src/components/_shared/UI/DropdownList';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import { useState } from 'react';
import JobTitle from './JobTitle';
import Input from "@src/components/_shared/UI/Input";
import { personUpdate } from "@src/api/Registration";
import { handleApiError } from "@src/utilities/functions";


export default function LegalAgentSituation() {
    const { setSection, setBlock } = useNavigationStore();
    const { setField, registration } = useRegistrationStore();
    const [notEmployed, setNotEmployed] = useState<boolean>(['SP4', 'SP5'].includes(registration.person.professional.professional_situation) || false);
    const [jobTitle, setJobTitle] = useState<boolean>(false);

    const { data, isLoading } = useQuery({
        queryKey: ['combinedReferencialData'],
        queryFn: async () => {
            const [situationsData, regimesData, professionalSituationsData, professionsData, domainsData] = await Promise.all([
                getReferencialData({ type: 'family/situations' }),
                getReferencialData({ type: 'family/regimes' }),
                getReferencialData({ type: 'profession/situations' }),
                getReferencialData({ type: 'professions' }),
                getReferencialData({ type: 'profession/domains' })
            ]);
            return { situationsData, regimesData, professionalSituationsData, professionsData, domainsData };
        }
    });

    const { mutate, isPending } = useMutation({
        mutationFn: () => personUpdate({key: 'family', value: registration.person.family, is_psi: registration.partner_informations.is_partner, person_id: registration.person_id}), 
        onSuccess: () => {
            if (!notEmployed) {
                setJobTitle(true)
            } else {
                setBlock('FinancialJob')
            }
        },
        onError: (error: any) => handleApiError(error)
    });

    const _onGoBack = () => {
        setSection(2)
        setBlock('USPerson')
    };

    const handleSelectFamilySituation = (value: string) => {
        // reset du regime si changement
        setField('person.family', {
            ...registration.person.family,
            matrimonial_regime: ''
        })
        setField('person.family', {
            ...registration.person.family,
            family_situation: value
        })
    };
    const handleSelectRegime = (value: string) => {
        setField('person.family', {
            ...registration.person.family,
            matrimonial_regime: value
        })
    }
    const handleSelectProfessionalSituation = (value: string) => {
        // reset des champs suivant si changement
        setField('person.professional', {
            ...registration.person.professional,
            activity_domain: '',
            profession: ''
        })
        setNotEmployed(false);

        if (['SP4', 'SP5'].includes(value)) {
            setField('person.professional', {
                ...registration.person.professional,
                profession: '82'
            })
            value === 'SP4' && setField('person.professional', {
                ...registration.person.professional,
                activity_domain: '14'
            })
            value === 'SP5' && setField('person.professional', {
                ...registration.person.professional,
                activity_domain: '25'
            })
            setNotEmployed(true);
        }
        setField('person.professional', {
            ...registration.person.professional,
            professional_situation: value
        })
    }
    const handleSelectProfession = (value: string) => {
        setField('person.professional', {
            ...registration.person.professional,
            profession: value
        })
    }
    const handleSelectActivityDomain = (value: string) => {
        setField('person.professional', {
            ...registration.person.professional,
            activity_domain: value
        })
    }

    const basicFieldsFilled = !!registration.person.family.family_situation && 
        !!registration.person.professional.professional_situation && 
        !!registration.person.professional.profession && 
        !!registration.person.professional.activity_domain &&
        !(registration.person.family.family_situation === 'M' && registration.person.family.matrimonial_regime === '');
    const activityDomainIsValid = ['99', '12', '21'].includes(registration.person.professional.activity_domain) 
        && registration.person.professional.other_domain;
    const isFullFilled = basicFieldsFilled || activityDomainIsValid;

    if (jobTitle) {
        return (
            <JobTitle {...{setJobTitle}} />
        )
    }
    return (
        <Container>
            <BackButton onClick={_onGoBack}/>
            <BlockTitle title="Situation du repésentant légal" />
            <Subtitle content="Veuillez compléter les informations suivantes" />
            <div style={{width: '100%'}}>
                {isLoading ? <Loader type='SPINNER' /> : null}
                {data?.situationsData ?
                    <DropdownList
                        title="Situation familiale"
                        width="100%"
                        options={data?.situationsData}
                        selected={registration.person.family.family_situation}
                        onSelect={handleSelectFamilySituation} />
                    : null}
                {registration.person.family.family_situation && registration.person.family.family_situation === "M" && data?.regimesData ?
                    <DropdownList
                        title="Régime matrimonial"
                        width="100%"
                        options={data?.regimesData}
                        selected={registration.person.family.matrimonial_regime}
                        onSelect={handleSelectRegime} />
                    : null}
                {(registration.person.family.matrimonial_regime || (registration.person.family.family_situation &&registration.person.family.family_situation !== "M")) && data?.professionalSituationsData ?
                    <DropdownList
                        title="Situation professionnelle"
                        width="100%"
                        options={data?.professionalSituationsData}
                        selected={registration.person.professional.professional_situation}
                        onSelect={handleSelectProfessionalSituation} />
                    : null}
                {registration.person.professional.professional_situation && data?.professionalSituationsData && !notEmployed ?
                    <DropdownList
                        title={registration.person.professional.professional_situation === 'SP2' ? 'Ancienne profession' : 'Profession'}
                        width="100%"
                        options={data?.professionsData}
                        selected={registration.person.professional.profession}
                        onSelect={handleSelectProfession} />
                    : null}
                {registration.person.professional.profession && data?.domainsData && !notEmployed ?
                    <DropdownList
                        title="Domaine d’activité"
                        width="100%"
                        options={data?.domainsData}
                        selected={registration.person.professional.activity_domain}
                        onSelect={handleSelectActivityDomain} />
                    : null}
                {registration.person.professional.activity_domain === '99' ?
                    <div style={{ maxWidth: '460px', margin: '0 auto' }}>
                        <Input
                            title="Autre domaine d’activité"
                            name="other_domain"
                            id="other_domain"
                            width="100%"
                            type="text"
                            placeholder="Saisissez le domaine"
                            value={registration.person.professional.other_domain}
                            onChange={(e) => setField('person.professional', { ...registration.person.professional, other_domain: e.target.value })}
                            clearInput={() => setField('person.professional', { ...registration.person.professional, other_domain: ""})}
                        />
                    </div>
                    : null}
                
                {isFullFilled ? <ValidateButton title="Continuer" onClick={() => mutate()} onLoading={isPending} /> : null}
            </div>
        </Container>
    )
}
